/**
 * Pickup - Collectable food item that grants fart ammo
 * 
 * Pickups spawn in the maze and can be collected by walking over them.
 * Each pickup type grants ammo for a specific fart type.
 */

import Phaser from 'phaser';
import { PickupType, getPickupConfig, getFartTypeFromPickup, getPickupAmmoAmount } from './farts/FartTypes';

export interface PickupConfig {
    id: string;  // Unique identifier for multiplayer sync
    pickupType: PickupType;
    x: number;
    y: number;
}

export class Pickup extends Phaser.Physics.Arcade.Sprite {
    public readonly id: string;
    public readonly pickupType: PickupType;
    private bobTween?: Phaser.Tweens.Tween;
    private glowGraphics?: Phaser.GameObjects.Graphics;

    constructor(scene: Phaser.Scene, config: PickupConfig) {
        const pickupConfig = getPickupConfig(config.pickupType);
        const textureKey = `pickup_${config.pickupType.toLowerCase()}`;

        super(scene, config.x, config.y, textureKey);

        this.id = config.id;
        this.pickupType = config.pickupType;

        scene.add.existing(this);
        scene.physics.add.existing(this);

        // Configure physics body
        const body = this.body as Phaser.Physics.Arcade.Body;
        body.setCircle(12);
        body.setOffset(0, 2);
        body.setImmovable(true);

        // Visual setup
        this.setDepth(25); // Above floor, below players
        this.setScale(1);

        // Create glow effect
        this.createGlowEffect(pickupConfig.color);

        // Start bobbing animation
        this.startBobAnimation();

        // Store data for collection
        this.setData('pickupType', config.pickupType);
        this.setData('ammoAmount', getPickupAmmoAmount(config.pickupType));
    }

    /**
     * Create a colored glow behind the pickup
     */
    private createGlowEffect(color: number): void {
        this.glowGraphics = this.scene.add.graphics();
        this.glowGraphics.setDepth(24);

        // Draw glow circle
        this.glowGraphics.fillStyle(color, 0.3);
        this.glowGraphics.fillCircle(0, 0, 18);
        this.glowGraphics.fillStyle(color, 0.15);
        this.glowGraphics.fillCircle(0, 0, 24);

        // Position at pickup location
        this.glowGraphics.setPosition(this.x, this.y);

        // Pulse the glow
        this.scene.tweens.add({
            targets: this.glowGraphics,
            alpha: { from: 1, to: 0.5 },
            duration: 1000,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut'
        });
    }

    /**
     * Start floating/bobbing animation
     */
    private startBobAnimation(): void {
        const startY = this.y;

        this.bobTween = this.scene.tweens.add({
            targets: this,
            y: startY - 4,
            duration: 800,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut'
        });
    }

    /**
     * Get the amount of ammo this pickup grants
     */
    getAmmoAmount(): number {
        return this.getData('ammoAmount') as number;
    }

    /**
     * Get the fart type this pickup grants ammo for
     */
    getFartType() {
        return getFartTypeFromPickup(this.pickupType);
    }

    /**
     * Play collection effect and destroy
     */
    collect(): void {
        // Prevent multiple collections
        if (!this.body || !this.body.enable) return;

        // Disable physics immediately
        if (this.body) {
            this.body.enable = false;
        }

        // Stop animations
        this.bobTween?.stop();

        // Collection particles/effect
        const config = getPickupConfig(this.pickupType);

        // Scale up and fade out
        this.scene.tweens.add({
            targets: this,
            scaleX: 1.5,
            scaleY: 1.5,
            alpha: 0,
            duration: 200,
            ease: 'Power2',
            onComplete: () => this.destroy()
        });

        // Glow expands and fades
        if (this.glowGraphics) {
            this.scene.tweens.add({
                targets: this.glowGraphics,
                scaleX: 2,
                scaleY: 2,
                alpha: 0,
                duration: 200,
                onComplete: () => {
                    this.glowGraphics?.destroy();
                }
            });
        }
    }

    /**
     * Clean up resources
     */
    destroy(fromScene?: boolean): void {
        this.bobTween?.stop();
        this.glowGraphics?.destroy();
        super.destroy(fromScene);
    }
}
